import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, faPlus, faSave, faTimes, faEdit, faTrash
} from '@fortawesome/free-solid-svg-icons';
import Layout from './Layout';
import './Settings.css';
import withAuth from '../utils/withAuth';
import profileService from '../services/profileService';
import AccountDatePicker from './ui/AccountDatePicker';
import CountryCodeSelect from './CountryCodeSelect';
import PhoneField, { isValidPhoneNumber } from './ui/PhoneField';
import AvatarPositionEditor from './ui/AvatarPositionEditor';
import logo from '../assets/images/IMT_logo.png';
import hmsAdminService from '../services/hmsAdminService';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import { getAbsoluteMediaUrl, addCacheBuster } from '../utils/mediaUtils';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import COUNTRIES from '../data/countries.json';
import { TEAM_CALENDAR_CONSENT_TEXT } from '../constants/teamCalendarConsent';

function AccountInformation() {
  const history = useHistory();
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Profile data state
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    languagesSpoken: [],
    workingCharacteristics: []
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Form state tracking
  const [hasChanges, setHasChanges] = useState(false);

  // Additional phone numbers state
  const [additionalPhoneNumbers, setAdditionalPhoneNumbers] = useState([]);
  const [phoneNumbersEditMode, setPhoneNumbersEditMode] = useState(false);
  
  // Languages dropdown state
  const [showLanguagesDropdown, setShowLanguagesDropdown] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const languagesRef = useRef(null);

  // Additional entities dropdown state
  const [showAdditionalOfficesDropdown, setShowAdditionalOfficesDropdown] = useState(false);
  const additionalOfficesRef = useRef(null);
  
  // Avatar upload options state
  const [showAvatarOptions, setShowAvatarOptions] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const avatarOptionsRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Position verification state
  const [positionRequests, setPositionRequests] = useState([]);
  const [selectedPositionRequiresVerification, setSelectedPositionRequiresVerification] = useState(false);
  const [showPositionWarning, setShowPositionWarning] = useState(false);
  const [pendingPositionRequest, setPendingPositionRequest] = useState(null);

  /**
   * Load position requests for the current user
   */
  const loadPositionRequests = useCallback(async () => {
    try {
      const requestsData = await profileService.getMyPositionRequests();
      setPositionRequests(requestsData.requests || []);
      
      // Check if there's a pending request
      const pending = requestsData.requests?.find(req => req.status === 'pending');
      setPendingPositionRequest(pending || null);
      
      console.log('📋 POSITION: Position requests loaded:', {
        total: requestsData.requests?.length || 0,
        hasPending: !!pending,
        pendingPosition: pending?.requested_position_display
      });
    } catch (error) {
      console.error('Error loading position requests:', error);
      // Don't show error for this - it's not critical
      setPositionRequests([]);
      setPendingPositionRequest(null);
    }
  }, []);

  /**
   * Load dropdown options (can be called independently to refresh)
   */
  const loadDropdownOptions = useCallback(async () => {
    console.log('🔄 DROPDOWN: Loading dropdown options');
    try {
      const options = await profileService.getDropdownOptions();
      console.log('📊 DROPDOWN: Raw options received:', options);
      console.log('📊 DROPDOWN: Positions in options:', options.positions);
      console.log('📊 DROPDOWN: First position details:', options.positions?.[0]);
      setDropdownOptions(options);
      console.log('✅ DROPDOWN: Dropdown options loaded and set');
      return options;
    } catch (error) {
      console.error('❌ DROPDOWN: Error loading dropdown options:', error);
      // Don't throw - allow the component to continue with existing options
      return dropdownOptions;
    }
  }, []);

  /**
   * Load profile data and dropdown options
   */
  const loadData = useCallback(async () => {
    console.log('🔄 PROFILE: Starting profile data load');
    try {
      setLoading(true);
      
      // Load profile, dropdown options, and position requests in parallel
      const [profileData, options] = await Promise.all([
        profileService.getProfile(),
        loadDropdownOptions(),
        loadPositionRequests().catch(() => {}) // Don't fail the whole load if position requests fail
      ]);
      
      console.log('📥 PROFILE: Raw profile data received:', profileData);
      
      const formattedProfile = profileService.formatProfileData(profileData);
      console.log('📊 PROFILE: Formatted profile data:', formattedProfile);
      console.log('🖼️ PROFILE: Avatar details:', {
        hasAvatar: formattedProfile.hasAvatar,
        avatarUrl: formattedProfile.avatarUrl,
        avatar_photo: profileData.avatar_photo
      });
      
      // Work/personal phone are stored and edited as E.164 (e.g. "+27123456789")
      // and consumed directly by PhoneField — no country-code splitting needed.

      // Initialize additional phone numbers
      if (formattedProfile.additional_phone_numbers && Array.isArray(formattedProfile.additional_phone_numbers)) {
        setAdditionalPhoneNumbers(formattedProfile.additional_phone_numbers);
        console.log('📞 PROFILE: Loaded additional phone numbers:', formattedProfile.additional_phone_numbers);
      } else {
        setAdditionalPhoneNumbers([]);
      }
      
      setProfile(formattedProfile);
      setFormData(formattedProfile);
      // Options already set by loadDropdownOptions
      
      console.log('✅ PROFILE: Profile data loaded and state updated');
      
    } catch (err) {
      console.error('❌ PROFILE: Error loading profile data:', err);
      showError(getErrorMessage(err, 'Failed to load profile information'));
    } finally {
      setLoading(false);
    }
  }, [loadDropdownOptions, loadPositionRequests]);

  /**
   * Reload dropdown options when window regains focus
   * This ensures fresh data after system data management edits
   */
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 PROFILE: Window focused, reloading dropdown options');
      loadDropdownOptions();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadDropdownOptions]);

  /**
   * Listen for system data updates and reload dropdown options
   * This ensures immediate refresh when positions/offices/departments are modified
   */
  useEffect(() => {
    const handleSystemDataUpdate = (event) => {
      console.log('🔔 PROFILE: System data updated, reloading dropdown options:', event.detail);
      loadDropdownOptions();
    };
    
    window.addEventListener('systemDataUpdated', handleSystemDataUpdate);
    return () => window.removeEventListener('systemDataUpdated', handleSystemDataUpdate);
  }, [loadDropdownOptions]);

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback((fieldName, value) => {
    console.log('📝 FIELD_CHANGE: Field changed:', {
      fieldName,
      newValue: value,
      valueType: typeof value,
      isArray: Array.isArray(value),
      isObject: value && typeof value === 'object' && !Array.isArray(value),
      timestamp: new Date().toISOString()
    });
    
    setFormData(prev => {
      console.log('📝 FIELD_CHANGE: Previous formData for field', fieldName, ':', prev[fieldName]);
      
      const updated = { ...prev, [fieldName]: value };
      setHasChanges(true);
      
      console.log('📝 FIELD_CHANGE: hasChanges set to true');
      
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (newErrors[fieldName]) {
          console.log('📝 FIELD_CHANGE: Clearing validation error for field:', fieldName);
          delete newErrors[fieldName]; // Clear field error on change
        }
        return newErrors;
      });
      
      console.log('📝 FIELD_CHANGE: Updated formData for field', fieldName, ':', updated[fieldName]);
      console.log('📝 FIELD_CHANGE: Form now has', Object.keys(updated).length, 'fields');
      
      // Auto-save functionality removed per user request
      
      return updated;
    });
  }, []);

  /**
   * Add a new additional phone number
   */
  const handleAddPhoneNumber = useCallback(() => {
    const newPhone = {
      id: Date.now().toString(),
      label: '',
      country_code: '+27',
      number: ''
    };
    setAdditionalPhoneNumbers(prev => [...prev, newPhone]);
    setHasChanges(true);
    console.log('📞 Added new phone number:', newPhone);
  }, []);

  /**
   * Update a specific additional phone number field
   */
  const handleUpdatePhoneNumber = useCallback((id, field, value) => {
    setAdditionalPhoneNumbers(prev => 
      prev.map(phone => 
        phone.id === id ? { ...phone, [field]: value } : phone
      )
    );
    setHasChanges(true);
    console.log('📞 Updated phone number:', { id, field, value });
  }, []);

  /**
   * Remove an additional phone number
   */
  const handleRemovePhoneNumber = useCallback((id) => {
    setAdditionalPhoneNumbers(prev => prev.filter(phone => phone.id !== id));
    setHasChanges(true);
    console.log('📞 Removed phone number:', id);
  }, []);

  /**
   * Handle position change with verification logic
   */
  const handlePositionChange = useCallback(async (newPositionCode) => {
    console.log('🔄 POSITION: Position change requested:', newPositionCode);
    
    // If empty selection, clear position
    if (!newPositionCode) {
      handleFieldChange('position', null);
      setSelectedPositionRequiresVerification(false);
      setShowPositionWarning(false);
      return;
    }
    
    // Find the position details from dropdown options
    const positionOption = dropdownOptions.positions?.find(pos => pos.value === newPositionCode);
    if (!positionOption) {
      console.error('❌ POSITION: Invalid position selected:', newPositionCode);
      return;
    }

    const requiresVerification = positionOption.requires_verification;
    console.log('🔍 POSITION: Position option found:', positionOption);
    console.log('🔍 POSITION: Position details:', {
      code: newPositionCode,
      id: positionOption.id,
      name: positionOption.label,
      requiresVerification
    });

    // Update state to show verification warning if needed
    setSelectedPositionRequiresVerification(requiresVerification);
    setShowPositionWarning(requiresVerification);

    // Store the position as an object (consistent with backend format)
    // CRITICAL: The dropdown options have { id, value (code), label (name), requires_verification }
    const positionObject = {
      id: positionOption.id,  // This MUST be present and valid!
      code: positionOption.value,
      name: positionOption.label,
      requires_verification: requiresVerification
    };
    
    console.log('✅ POSITION: Created position object:', positionObject);
    
    if (!requiresVerification) {
      console.log('✅ POSITION: No verification required, updating form data with position object');
      handleFieldChange('position', positionObject);
      return;
    }

    // For positions requiring verification, show warning and update form data for UI consistency
    console.log('⚠️ POSITION: Verification required, showing warning');
    handleFieldChange('position', positionObject);
    
  }, [dropdownOptions.positions, handleFieldChange]);

  /**
   * Handle position save with verification request
   */
  const handlePositionSave = useCallback(async (positionObject) => {
    console.log('💾 POSITION: Saving position with verification:', positionObject);
    
    // Extract the position code from the position object
    const positionCode = positionObject?.code || positionObject;
    console.log('💾 POSITION: Position code to send:', positionCode);
    
    try {
      setSaving(true);
      
      const result = await profileService.requestPositionChange(positionCode);
      console.log('✅ POSITION: Position request result:', result);
      
      if (result.requires_verification) {
        // Position requires verification - show success message
        showSuccess(`Position change request submitted for ${result.position_display}. Admin verification required (3-day review period).`);
        
        // Reload position requests to show the new pending request
        await loadPositionRequests();
        
        // Reset the position warning
        setShowPositionWarning(false);
        setSelectedPositionRequiresVerification(false);
        
      } else {
        // Position was updated directly
        showSuccess(`Position updated successfully to ${result.position_display}.`);
        
        // Reload the profile to get the updated position object
        await loadData();
        setHasChanges(false);
        
        // Reset states
        setShowPositionWarning(false);
        setSelectedPositionRequiresVerification(false);
      }
      
    } catch (error) {
      console.error('❌ POSITION: Error saving position:', error);
      showError(getErrorMessage(error, 'Failed to update account information. Please try again.'));
    } finally {
      setSaving(false);
    }
  }, [loadPositionRequests]);


  /**
   * Handle manual save
   */
  const handleSave = async () => {
    console.log('💾 SAVE: Save button clicked - starting save process');
    console.log('💾 SAVE: Current form state:', {
      hasChanges,
      saving,
      showPositionWarning,
      selectedPositionRequiresVerification,
      formDataKeys: Object.keys(formData),
      formDataSize: JSON.stringify(formData).length
    });
    
    // Handle position verification request separately
    if (showPositionWarning && selectedPositionRequiresVerification) {
      console.log('🔄 SAVE: Position verification required, handling position request');
      await handlePositionSave(formData.position);
      return;
    }
    
    try {
      console.log('💾 SAVE: Setting saving state and clearing errors');
      setSaving(true);
      setValidationErrors({});
      
      console.log('💾 SAVE: Current formData before preparation:', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        preferred_name: formData.preferred_name,
        office: formData.office,
        department: formData.department,
        position: formData.position,
        dateOfBirth: formData.dateOfBirth,
        businessContexts: formData.businessContexts,
        languagesSpoken: formData.languagesSpoken,
        workingCharacteristics: formData.workingCharacteristics,
        work_phone: formData.work_phone,
        work_email: formData.work_email,
        personal_phone: formData.personal_phone,
        working_guide_custom: formData.working_guide_custom
      });
      
      // Work/personal phone are already E.164 (PhoneField stores them that way).
      // Validate before sending so bad numbers surface inline, not as a 400.
      const phoneErrors = {};
      if (formData.work_phone && !isValidPhoneNumber(formData.work_phone)) {
        phoneErrors.work_phone = ['Enter a valid phone number including country code.'];
      }
      if (formData.personal_phone && !isValidPhoneNumber(formData.personal_phone)) {
        phoneErrors.personal_phone = ['Enter a valid phone number including country code.'];
      }
      if (Object.keys(phoneErrors).length > 0) {
        setValidationErrors((prev) => ({ ...prev, ...phoneErrors }));
        showError('Please correct the errors below');
        setSaving(false);
        return;
      }

      const formDataWithE164Phones = { ...formData };

      // Include additional phone numbers (already formatted with country codes)
      formDataWithE164Phones.additional_phone_numbers = additionalPhoneNumbers;
      console.log('📞 SAVE: Including additional phone numbers:', additionalPhoneNumbers);
      
      console.log('💾 SAVE: Preparing update data via profileService.prepareUpdateData()');
      const updateData = profileService.prepareUpdateData(formDataWithE164Phones);
      console.log('💾 SAVE: Prepared update data:', updateData);
      console.log('💾 SAVE: Update data size:', JSON.stringify(updateData).length, 'characters');
      
      console.log('💾 SAVE: Calling profileService.updateProfile() with prepared data');
      const startTime = performance.now();
      const result = await profileService.updateProfile(updateData);
      const endTime = performance.now();
      console.log('💾 SAVE: API call completed in', (endTime - startTime).toFixed(2), 'ms');
      
      console.log('💾 SAVE: Received result from profileService:', {
        success: result.success,
        hasData: !!result.data,
        hasErrors: !!result.errors,
        error: result.error,
        message: result.message
      });
      
      if (result.success) {
        console.log('✅ SAVE: Save operation successful');
        console.log('✅ SAVE: Raw server response data:', result.data);
        
        const updatedProfile = profileService.formatProfileData(result.data);
        console.log('✅ SAVE: Formatted profile data:', {
          displayName: updatedProfile.displayName,
          hasAvatar: updatedProfile.hasAvatar,
          office: updatedProfile.office,
          department: updatedProfile.department,
          dateOfBirth: updatedProfile.dateOfBirth,
          languagesSpoken: updatedProfile.languagesSpoken,
          workingCharacteristics: updatedProfile.workingCharacteristics
        });
        
        // Phones come back as E.164 and are used as-is by PhoneField.

        console.log('✅ SAVE: Updating component state with new profile data');
        console.log('✅ SAVE: Previous formData languagesSpoken:', formData.languagesSpoken);
        console.log('✅ SAVE: Previous formData workingCharacteristics:', formData.workingCharacteristics);
        console.log('✅ SAVE: New formData languagesSpoken:', updatedProfile.languagesSpoken);
        console.log('✅ SAVE: New formData workingCharacteristics:', updatedProfile.workingCharacteristics);
        
        setProfile(updatedProfile);
        setFormData(updatedProfile);
        setHasChanges(false);
        setPhoneNumbersEditMode(false);
        showSuccess('Profile updated successfully!');
        
        console.log('✅ SAVE: Profile state updated successfully');
      } else {
        console.log('❌ SAVE: Save operation failed');
        console.log('❌ SAVE: Failure details:', {
          errors: result.errors,
          error: result.error,
          message: result.message
        });
        
        if (result.errors) {
          console.log('❌ SAVE: Setting validation errors:', result.errors);
          setValidationErrors(result.errors);
          showError('Please correct the errors below');
          
          // Log each validation error for debugging
          Object.entries(result.errors).forEach(([field, errors]) => {
            console.log(`❌ SAVE: Validation error for ${field}:`, errors);
          });
        } else {
          const errorMessage = result.error || 'Failed to update profile';
          console.log('❌ SAVE: Setting general error message:', errorMessage);
          showError(errorMessage);
        }
      }
    } catch (err) {
      console.error('❌ SAVE: Exception caught during save operation:', err);
      console.error('❌ SAVE: Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errorMessage = getErrorMessage(err, 'Failed to update profile');
      console.log('❌ SAVE: Setting error message from exception:', errorMessage);
      showError(errorMessage);
    } finally {
      console.log('💾 SAVE: Save operation completed, resetting saving state');
      setSaving(false);
      
      console.log('💾 SAVE: Final component state:', {
        saving: false,
        hasChanges,
        validationErrorsCount: Object.keys(validationErrors).length
      });
    }
  };

  /**
   * Handle camera capture option
   */
  const handleCameraCapture = async () => {
    try {
      setShowAvatarOptions(false);
      // Clear any previous errors handled by flash messages
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showError('Camera access is not supported in this browser.');
        return;
      }
      
      setShowCameraModal(true);
      
      // Wait for modal to render before accessing video element
      setTimeout(async () => {
        try {
          // Request camera permissions with more flexible constraints
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              width: { ideal: 400 },
              height: { ideal: 400 },
              facingMode: 'user'
            },
            audio: false
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true; // Required for autoplay
            videoRef.current.playsInline = true; // Better mobile support
            
            // Multiple approaches to ensure video plays
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play().catch(err => {
                console.error('Video play error:', err);
                // Try again after a short delay
                setTimeout(() => {
                  videoRef.current.play().catch(console.error);
                }, 100);
              });
            };
            
            // Also try to play immediately
            setTimeout(() => {
              if (videoRef.current && videoRef.current.srcObject) {
                videoRef.current.play().catch(console.error);
              }
            }, 200);
          }
        } catch (streamError) {
          console.error('Camera stream error:', streamError);
          showError('Unable to access camera. Please check permissions and try again.');
          setShowCameraModal(false);
        }
      }, 100);
      
    } catch (err) {
      console.error('Camera access error:', err);
      showError('Unable to access camera. Please check permissions and try again.');
      setShowCameraModal(false);
    }
  };

  /**
   * Capture photo from camera
   */
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Set canvas dimensions
      canvas.width = 400;
      canvas.height = 400;
      
      // Draw video frame to canvas
      context.drawImage(video, 0, 0, 400, 400);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create file from blob
          const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
          handleAvatarUpload(file);
        }
      }, 'image/jpeg', 0.8);
      
      // Stop camera stream
      const stream = video.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      setShowCameraModal(false);
    }
  };

  /**
   * Close camera modal
   */
  const closeCameraModal = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCameraModal(false);
  };

  /**
   * Handle file selection option
   */
  const handleFileSelection = () => {
    setShowAvatarOptions(false);
    document.getElementById('top-avatar-upload').click();
  };

  /**
   * Handle file input change - Show positioning editor
   */
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file before showing editor
      const validation = profileService.validateAvatarFile(file);
      if (!validation.valid) {
        showError(validation.error);
        return;
      }
      
      // Show positioning editor
      setSelectedAvatarFile(file);
      setShowAvatarEditor(true);
    }
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  /**
   * Handle avatar editor save - Upload the positioned image
   */
  const handleAvatarEditorSave = async (positionedFile) => {
    setShowAvatarEditor(false);
    setSelectedAvatarFile(null);
    
    // Upload the positioned file
    await handleAvatarUpload(positionedFile);
  };

  /**
   * Handle avatar editor cancel
   */
  const handleAvatarEditorCancel = () => {
    setShowAvatarEditor(false);
    setSelectedAvatarFile(null);
  };

  /**
   * Handle avatar upload
   */
  const handleAvatarUpload = async (file) => {
    console.log('🚀 AVATAR: Starting avatar upload process');
    console.log('📁 AVATAR: File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    // Validate file
    const validation = profileService.validateAvatarFile(file);
    if (!validation.valid) {
      console.log('❌ AVATAR: File validation failed:', validation.error);
      showError(validation.error);
      return;
    }
    
    console.log('✅ AVATAR: File validation passed');
    
    try {
      setUploadingAvatar(true);
      
      console.log('📤 AVATAR: Calling profileService.uploadAvatar...');
      const result = await profileService.uploadAvatar(file);
      
      console.log('📥 AVATAR: Upload result received:', result);
      
      if (result.success) {
        console.log('✅ AVATAR: Upload successful');
        console.log('🔗 AVATAR: Original avatar URL:', result.avatarUrl);
        
        // Ensure we have a full URL using the centralized utility
        const baseAvatarUrl = getAbsoluteMediaUrl(result.avatarUrl);
        
        // Create a unique timestamp for cache busting
        const avatarUrl = addCacheBuster(baseAvatarUrl);
        
        console.log('🔗 AVATAR: Cache-busted avatar URL:', avatarUrl);
        console.log('📊 AVATAR: Current profile state before update:', profile);
        console.log('📊 AVATAR: Current formData state before update:', formData);
        
        // Force immediate state update with React.flushSync equivalent
        const updatedProfile = { ...profile, avatarUrl: avatarUrl, hasAvatar: true };
        const updatedFormData = {
          ...formData,
          avatarUrl: avatarUrl,
          hasAvatar: true
        };
        
        console.log('📊 AVATAR: Updated profile state:', updatedProfile);
        console.log('📊 AVATAR: Updated formData state:', updatedFormData);
        
        // Update both states immediately
        setProfile(updatedProfile);
        setFormData(updatedFormData);
        
        console.log('🔄 AVATAR: State updates called');
        
        // Force a re-render by updating a dummy state if needed
        showSuccess('Avatar uploaded successfully');
        
        // Additional cache busting: preload the new image
        const img = new Image();
        img.onload = () => {
          console.log('✅ AVATAR: New avatar image preloaded successfully');
        };
        img.onerror = () => {
          console.log('❌ AVATAR: Failed to preload new avatar image');
        };
        img.src = avatarUrl;
        console.log('🖼️ AVATAR: Started preloading image:', avatarUrl);
      } else {
        console.log('❌ AVATAR: Upload failed:', result.error);
        showError(result.error || 'Failed to upload avatar');
      }
    } catch (err) {
      console.error('❌ AVATAR: Avatar upload error:', err);
      showError(getErrorMessage(err, 'Failed to upload avatar'));
    } finally {
      setUploadingAvatar(false);
      console.log('🏁 AVATAR: Avatar upload process completed');
    }
  };

  /**
   * Handle working characteristics change
   */
  const handleWorkingCharacteristicsChange = (characteristic, checked) => {
    const current = formData.workingCharacteristics || [];
    let updated;
    
    if (checked) {
      // Add if not already present and under limit
      if (!current.includes(characteristic) && current.length < 10) {
        updated = [...current, characteristic];
      } else {
        updated = current;
      }
    } else {
      // Remove if present
      updated = current.filter(c => c !== characteristic);
    }
    
    handleFieldChange('workingCharacteristics', updated);
  };

  /**
   * Handle multi-select languages change
   */
  const handleLanguagesChange = (selectedLanguages) => {
    handleFieldChange('languagesSpoken', selectedLanguages);
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languagesRef.current && !languagesRef.current.contains(event.target)) {
        setShowLanguagesDropdown(false);
        setLanguageSearch('');
      }
      if (additionalOfficesRef.current && !additionalOfficesRef.current.contains(event.target)) {
        setShowAdditionalOfficesDropdown(false);
      }
      if (avatarOptionsRef.current && !avatarOptionsRef.current.contains(event.target)) {
        setShowAvatarOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="account-information-layout">
          <div className="account-information-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your profile...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="account-information-layout">
        {/* HMS Header */}
        <div className="account-information-header">
          <div className="header-logo">
            <img src={logo} alt="IMT" className="header-logo-img" />
            <div>
              <h1 className="hms-screen-title">Account Information</h1>
              <p className="account-subtitle">Your personal IMT profile and business information</p>
            </div>
          </div>
          <div className="settings-actions">
            {hasChanges && (
              <span className="unsaved-changes">Unsaved changes</span>
            )}
            <button 
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`account-save-button ${saving ? 'saving' : ''}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        
        <div className="account-information-content">
          <div className="account-information-container">

            {/* Flash messages are now handled by the FlashMessageProvider */}

              {/* Avatar Position Editor */}
            {showAvatarEditor && selectedAvatarFile && (
              <AvatarPositionEditor
                file={selectedAvatarFile}
                onSave={handleAvatarEditorSave}
                onCancel={handleAvatarEditorCancel}
              />
            )}

            {/* Camera Modal */}
            {showCameraModal && (
              <div className="camera-modal-overlay">
                <div className="camera-modal">
                  <div className="camera-modal-header">
                    <h3>Capture Profile Photo</h3>
                    <button className="camera-close-button" onClick={closeCameraModal}>×</button>
                  </div>
                  <div className="camera-modal-body">
                    <video 
                      ref={videoRef} 
                      className="camera-video" 
                      autoPlay 
                      playsInline 
                      muted 
                      controls={false}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </div>
                  <div className="camera-modal-footer">
                    <button className="camera-capture-button" onClick={capturePhoto}>
                      Capture Photo
                    </button>
                    <button className="camera-cancel-button" onClick={closeCameraModal}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

              <div className="profile-form">
              {/* Personal Information Section */}
              <div className="form-section">
                <h2>Personal Information</h2>
              
              {/* Avatar Section - Inside Personal Information */}
              <div className="top-avatar-section">
                <div className="top-avatar-container">
                  <div className="top-avatar-preview">
                    {(() => {
            
                      if (formData.hasAvatar) {
                        return (
                          <img 
                            key={formData.avatarUrl}
                            src={formData.avatarUrl} 
                            alt="Profile avatar" 
                            className="top-avatar-image"

                          />
                        );
                      } else {
                        console.log('❌ AVATAR RENDER: Displaying placeholder');
                        return (
                          <div className="top-avatar-placeholder">
                            <span>{formData.displayName?.charAt(0) || 'U'}</span>
                          </div>
                        );
                      }
                    })()}
                  </div>
                  
                  {/* Avatar Upload Controls - Top Right */}
                  <div className="top-avatar-controls" ref={avatarOptionsRef}>
                    <input
                      type="file"
                      id="top-avatar-upload"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                    <button 
                      onClick={() => setShowAvatarOptions(!showAvatarOptions)}
                      className={`top-upload-button-small ${uploadingAvatar ? 'uploading' : ''}`}
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? 'Uploading...' : 'Update Avatar'}
                    </button>
                    
                    {/* Avatar Options Dropdown */}
                    {showAvatarOptions && (
                      <div className="avatar-options-dropdown">
                        <button 
                          className="avatar-option-button"
                          onClick={handleCameraCapture}
                        >
                          Capture Image
                        </button>
                        <button 
                          className="avatar-option-button"
                          onClick={handleFileSelection}
                        >
                          Select Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="first_name">Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    value={formData.first_name || ''}
                    onChange={(e) => handleFieldChange('first_name', e.target.value)}
                    className={validationErrors.first_name ? 'error' : ''}
                    placeholder="Enter your first name"
                  />
                  {validationErrors.first_name && (
                    <span className="error-text">{validationErrors.first_name[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Surname *</label>
                  <input
                    type="text"
                    id="last_name"
                    value={formData.last_name || ''}
                    onChange={(e) => handleFieldChange('last_name', e.target.value)}
                    className={validationErrors.last_name ? 'error' : ''}
                    placeholder="Enter your surname"
                  />
                  {validationErrors.last_name && (
                    <span className="error-text">{validationErrors.last_name[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="preferred_name">Preferred Name</label>
                  <input
                    type="text"
                    id="preferred_name"
                    value={formData.preferred_name || ''}
                    onChange={(e) => handleFieldChange('preferred_name', e.target.value)}
                    placeholder="Enter your preferred name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="short_name">Short Name</label>
                  <input
                    type="text"
                    id="short_name"
                    value={formData.short_name || ''}
                    disabled
                    placeholder="Admin-assigned nickname"
                    title="Only administrators can modify this field"
                    className="read-only-field"
                  />
                  <span className="field-help-text">This field can only be set by administrators</span>
                </div>

                <div className="form-group">
                  <AccountDatePicker
                    id="date_of_birth"
                    name="date_of_birth"
                    value={formData.dateOfBirth ? (
                      formData.dateOfBirth instanceof Date && !isNaN(formData.dateOfBirth.getTime()) 
                        ? `${formData.dateOfBirth.getDate().toString().padStart(2, '0')}/${(formData.dateOfBirth.getMonth() + 1).toString().padStart(2, '0')}/${formData.dateOfBirth.getFullYear()}`
                        : ''
                    ) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        try {
                          // Parse DD/MM/YYYY format (full birthday with year)
                          if (e.target.value.includes('/')) {
                            const parts = e.target.value.split('/');
                            if (parts.length === 3) {
                              // DD/MM/YYYY format with actual birth year
                              const day = parseInt(parts[0]);
                              const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                              const year = parseInt(parts[2]);
                              const date = new Date(year, month, day);
                              
                              if (!isNaN(date.getTime()) && 
                                  date.getDate() === day && 
                                  date.getMonth() === month && 
                                  date.getFullYear() === year) {
                                handleFieldChange('dateOfBirth', date);
                              } else {
                                handleFieldChange('dateOfBirth', null);
                              }
                            } else if (parts.length === 2) {
                              // Legacy DD/MM format support - use fixed year (2000)
                              const day = parseInt(parts[0]);
                              const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                              const year = 2000; // Fixed year for legacy data
                              const date = new Date(year, month, day);
                              
                              if (!isNaN(date.getTime()) && 
                                  date.getDate() === day && 
                                  date.getMonth() === month) {
                                handleFieldChange('dateOfBirth', date);
                              } else {
                                handleFieldChange('dateOfBirth', null);
                              }
                            } else {
                              handleFieldChange('dateOfBirth', null);
                            }
                          } else {
                            // Handle ISO format (YYYY-MM-DD) for API data
                            const parts = e.target.value.split('-');
                            if (parts.length === 3) {
                              const year = parseInt(parts[0]);
                              const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                              const day = parseInt(parts[2]);
                              const date = new Date(year, month, day);
                              
                              if (!isNaN(date.getTime()) && 
                                  date.getDate() === day && 
                                  date.getMonth() === month && 
                                  date.getFullYear() === year) {
                                handleFieldChange('dateOfBirth', date);
                              } else {
                                handleFieldChange('dateOfBirth', null);
                              }
                            } else {
                              handleFieldChange('dateOfBirth', null);
                            }
                          }
                        } catch (error) {
                          console.error('Birthday parsing error:', error);
                          handleFieldChange('dateOfBirth', null);
                        }
                      } else {
                        handleFieldChange('dateOfBirth', null);
                      }
                    }}
                    placeholder="Select your birthday"
                    className={validationErrors.date_of_birth ? 'error' : ''}
                    format="DD/MM/YYYY"
                    label="Birthday"
                    required={false}
                  />
                  {validationErrors.date_of_birth && (
                    <span className="error-text">{validationErrors.date_of_birth[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <AccountDatePicker
                    id="date_joined_hms"
                    name="date_joined_hms"
                    value={formData.dateJoinedHMS ? (
                      formData.dateJoinedHMS instanceof Date && !isNaN(formData.dateJoinedHMS.getTime()) 
                        ? `${formData.dateJoinedHMS.getDate().toString().padStart(2, '0')}/${(formData.dateJoinedHMS.getMonth() + 1).toString().padStart(2, '0')}/${formData.dateJoinedHMS.getFullYear()}`
                        : ''
                    ) : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        try {
                          // Parse DD/MM/YYYY format (full date with year)
                          if (e.target.value.includes('/')) {
                            const parts = e.target.value.split('/');
                            if (parts.length === 3) {
                              const day = parseInt(parts[0]);
                              const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                              const year = parseInt(parts[2]);
                              const date = new Date(year, month, day);
                              
                              if (!isNaN(date.getTime()) && 
                                  date.getDate() === day && 
                                  date.getMonth() === month && 
                                  date.getFullYear() === year) {
                                handleFieldChange('dateJoinedHMS', date);
                              } else {
                                handleFieldChange('dateJoinedHMS', null);
                              }
                            } else {
                              handleFieldChange('dateJoinedHMS', null);
                            }
                          } else {
                            // Handle ISO format (YYYY-MM-DD) for API data
                            const parts = e.target.value.split('-');
                            if (parts.length === 3) {
                              const year = parseInt(parts[0]);
                              const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                              const day = parseInt(parts[2]);
                              const date = new Date(year, month, day);
                              
                              if (!isNaN(date.getTime()) && 
                                  date.getDate() === day && 
                                  date.getMonth() === month && 
                                  date.getFullYear() === year) {
                                handleFieldChange('dateJoinedHMS', date);
                              } else {
                                handleFieldChange('dateJoinedHMS', null);
                              }
                            } else {
                              handleFieldChange('dateJoinedHMS', null);
                            }
                          }
                        } catch (error) {
                          console.error('Date joined HMS parsing error:', error);
                          handleFieldChange('dateJoinedHMS', null);
                        }
                      } else {
                        handleFieldChange('dateJoinedHMS', null);
                      }
                    }}
                    placeholder="Select when you joined IMT"
                    className={validationErrors.date_joined_hms ? 'error' : ''}
                    format="DD/MM/YYYY"
                    label="Joined IMT"
                    required={false}
                  />
                  {validationErrors.date_joined_hms && (
                    <span className="error-text">{validationErrors.date_joined_hms[0]}</span>
                  )}
                </div>
                </div>
              </div>

              {/* Business Context Section */}
              <div className="form-section">
                <h2>Business Context</h2>
              <div className="business-type-checkbox-group">
                <label className="business-type-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.businessContexts?.solidFuels || false}
                    onChange={(e) => handleFieldChange('businessContexts', {
                      ...formData.businessContexts,
                      solidFuels: e.target.checked
                    })}
                  />
                  <span>Solid fuels</span>
                </label>
                <label className="business-type-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.businessContexts?.liquidFuels || false}
                    onChange={(e) => handleFieldChange('businessContexts', {
                      ...formData.businessContexts,
                      liquidFuels: e.target.checked
                    })}
                  />
                  <span>Liquid fuels</span>
                </label>
                </div>
              </div>

              {/* Team calendar consent (GDPR) */}
              <div className="form-section">
                <h2>Team Calendar</h2>
                <div className="business-type-checkbox-group">
                  <label className="business-type-checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.team_calendar_consent)}
                      onChange={(e) => handleFieldChange('team_calendar_consent', e.target.checked)}
                    />
                    <span>{TEAM_CALENDAR_CONSENT_TEXT}</span>
                  </label>
                </div>
              </div>

              {/* Languages Section */}
              <div className="form-section">
                <h2>Languages Spoken</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">Select Languages</label>
                  <div className="additional-entities-select" ref={languagesRef}>
                    <div
                      className={`additional-entities-select-header ${showLanguagesDropdown ? 'active' : ''} ${(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? 'placeholder' : ''}`}
                      onClick={() => {
                        const next = !showLanguagesDropdown;
                        setShowLanguagesDropdown(next);
                        if (!next) setLanguageSearch('');
                      }}
                    >
                      <span>
                        {!formData.languagesSpoken || formData.languagesSpoken.length === 0
                          ? 'Select languages spoken'
                          : `${formData.languagesSpoken.length} language${formData.languagesSpoken.length > 1 ? 's' : ''} selected`}
                      </span>
                    </div>

                    {showLanguagesDropdown && (() => {
                      const q = languageSearch.trim().toLowerCase();
                      const filteredLanguages = (dropdownOptions.languages || []).filter(lang => {
                        if (!q) return true;
                        return (
                          (lang.label || '').toLowerCase().includes(q) ||
                          (lang.value || '').toLowerCase().includes(q)
                        );
                      });
                      return (
                        <div className="additional-entities-dropdown">
                          <div className="additional-entities-search">
                            <input
                              type="text"
                              value={languageSearch}
                              onChange={(e) => setLanguageSearch(e.target.value)}
                              placeholder="Search languages..."
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredLanguages.length === 0 ? (
                            <div className="no-results">No languages match "{languageSearch}"</div>
                          ) : (
                            filteredLanguages.map(lang => {
                              const selected = formData.languagesSpoken?.includes(lang.value) || false;
                              return (
                                <label key={lang.value} className={`additional-entities-option ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = formData.languagesSpoken || [];
                                      const updated = e.target.checked
                                        ? [...current, lang.value]
                                        : current.filter(l => l !== lang.value);
                                      handleLanguagesChange(updated);
                                    }}
                                  />
                                  <span className="ae-checkbox" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="5 12 10 17 19 7" />
                                    </svg>
                                  </span>
                                  <span className="ae-label">{lang.label}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    className={`additional-entities-tags ${(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? 'empty' : ''}`}
                  >
                    {(!formData.languagesSpoken || formData.languagesSpoken.length === 0) ? (
                      <span className="empty-placeholder">No languages selected</span>
                    ) : (
                      formData.languagesSpoken.map(langValue => {
                        const lang = dropdownOptions.languages?.find(l => l.value === langValue);
                        if (!lang) return null;
                        return (
                          <span key={langValue} className="additional-entity-chip">
                            {lang.label}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = (formData.languagesSpoken || []).filter(l => l !== langValue);
                                handleLanguagesChange(updated);
                              }}
                              className="chip-remove"
                              aria-label={`Remove ${lang.label}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                </div>
              </div>

              {/* Organization Section */}
              <div className="form-section">
                <h2>Organization</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="office">Main Entity</label>
                  <select
                    id="office"
                    value={formData.office?.id || ''}
                    onChange={(e) => {
                      const selectedOffice = dropdownOptions.offices?.find(o => o.id === parseInt(e.target.value));
                      handleFieldChange('office', selectedOffice || null);
                      if (selectedOffice) {
                        const currentAdditional = formData.additional_offices || [];
                        const filtered = currentAdditional.filter(o => o.id !== selectedOffice.id);
                        if (filtered.length !== currentAdditional.length) {
                          handleFieldChange('additional_offices', filtered);
                        }
                      }
                    }}
                    className={validationErrors.office_id ? 'error' : ''}
                  >
                    <option value="">Select Main Entity</option>
                    {dropdownOptions.offices?.map(office => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.office_id && (
                    <span className="error-text">{validationErrors.office_id[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Additional Entities</label>
                  <div className="additional-entities-select" ref={additionalOfficesRef}>
                    <div
                      className={`additional-entities-select-header ${showAdditionalOfficesDropdown ? 'active' : ''} ${(!formData.additional_offices || formData.additional_offices.length === 0) ? 'placeholder' : ''}`}
                      onClick={() => setShowAdditionalOfficesDropdown(!showAdditionalOfficesDropdown)}
                    >
                      <span>
                        {!formData.additional_offices || formData.additional_offices.length === 0
                          ? 'Select additional entities'
                          : `${formData.additional_offices.length} additional ${formData.additional_offices.length === 1 ? 'entity' : 'entities'} selected`}
                      </span>
                    </div>

                    {showAdditionalOfficesDropdown && (() => {
                      const availableOffices = dropdownOptions.offices?.filter(o => o.id !== formData.office?.id) || [];
                      return (
                        <div className="additional-entities-dropdown">
                          {availableOffices.length === 0 ? (
                            <div className="additional-entities-option empty">
                              <span>No other entities available</span>
                            </div>
                          ) : (
                            availableOffices.map(office => {
                              const selected = (formData.additional_offices || []).some(ao => ao.id === office.id);
                              return (
                                <label key={office.id} className={`additional-entities-option ${selected ? 'selected' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = formData.additional_offices || [];
                                      const updated = e.target.checked
                                        ? [...current, office]
                                        : current.filter(ao => ao.id !== office.id);
                                      handleFieldChange('additional_offices', updated);
                                    }}
                                  />
                                  <span className="ae-checkbox" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="5 12 10 17 19 7" />
                                    </svg>
                                  </span>
                                  <span className="ae-label">{office.name}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    className={`additional-entities-tags ${(!formData.additional_offices || formData.additional_offices.length === 0) ? 'empty' : ''}`}
                  >
                    {(!formData.additional_offices || formData.additional_offices.length === 0) ? (
                      <span className="empty-placeholder">No additional entities selected</span>
                    ) : (
                      formData.additional_offices.map(office => (
                        <span key={office.id} className="additional-entity-chip">
                          {office.name}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (formData.additional_offices || []).filter(ao => ao.id !== office.id);
                              handleFieldChange('additional_offices', updated);
                            }}
                            className="chip-remove"
                            aria-label={`Remove ${office.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <select
                    id="department"
                    value={formData.department?.id || ''}
                    onChange={(e) => {
                      const selectedDept = dropdownOptions.departments?.find(d => d.id === parseInt(e.target.value));
                      handleFieldChange('department', selectedDept || null);
                    }}
                    className={validationErrors.department_id ? 'error' : ''}
                  >
                    <option value="">Select Department</option>
                    {dropdownOptions.departments?.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.department_id && (
                    <span className="error-text">{validationErrors.department_id[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="position">Position</label>
                  <select
                    id="position"
                    value={formData.position?.code || ''}
                    onChange={(e) => handlePositionChange(e.target.value)}
                    className={validationErrors.position ? 'error' : ''}
                    disabled={!!pendingPositionRequest}
                  >
                    <option value="">Select Position</option>
                    {dropdownOptions.positions?.map(pos => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.position && (
                    <span className="error-text">{validationErrors.position[0]}</span>
                  )}
                  
                  {/* Position verification warning */}
                  {showPositionWarning && selectedPositionRequiresVerification && (
                    <div className="position-verification-warning">
                      <div className="warning-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                          <path d="M12 9v4"/>
                          <path d="m12 17 .01 0"/>
                        </svg>
                      </div>
                      <div className="warning-content">
                        <p><strong>Admin Verification Required</strong></p>
                        <p>This position requires admin approval. Your request will be reviewed within 3 days.</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending position request status */}
                  {pendingPositionRequest && (
                    <div className="position-pending-status">
                      <div className="status-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12,6 12,12 16,14"/>
                        </svg>
                      </div>
                      <div className="status-content">
                        <p><strong>Position Change Pending</strong></p>
                        <p>You have a pending request for <strong>{pendingPositionRequest.requested_position_display}</strong></p>
                        <p className="status-details">
                          Submitted: {new Date(pendingPositionRequest.requested_at).toLocaleDateString()}
                          {' • '}
                          Expires: {new Date(pendingPositionRequest.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                  
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location</label>
                  <select
                    id="location"
                    value={formData.location || ''}
                    onChange={(e) => handleFieldChange('location', e.target.value)}
                    className={validationErrors.location ? 'error' : ''}
                  >
                    <option value="">Select Location</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.location && (
                    <span className="error-text">{validationErrors.location[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="supervisors">Supervisors</label>
                  <textarea
                    id="supervisors"
                    value={
                      formData.supervisors && formData.supervisors.length > 0
                        ? formData.supervisors.map(sup => sup.name).join(', ')
                        : 'Not set'
                    }
                    disabled
                    className="disabled-input"
                    rows={2}
                    style={{ resize: 'none' }}
                  />
                  <span className="field-help-text">This field can only be set by administrators</span>
                </div>

                <div className="form-group">
                  <label htmlFor="heads_of_department">Heads of Department</label>
                  <textarea
                    id="heads_of_department"
                    value={
                      formData.heads_of_department && formData.heads_of_department.length > 0
                        ? formData.heads_of_department.map(hod => hod.name).join(', ')
                        : 'Not set'
                    }
                    disabled
                    className="disabled-input"
                    rows={2}
                    style={{ resize: 'none' }}
                  />
                  <span className="field-help-text">This field can only be set by administrators</span>
                </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="form-section">
                <h2>Contact Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <PhoneField
                    id="work_phone"
                    label="Work Phone"
                    value={formData.work_phone}
                    onChange={(v) => handleFieldChange('work_phone', v)}
                    error={validationErrors.work_phone && validationErrors.work_phone[0]}
                    placeholder="Enter work phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="work_email">Work Email</label>
                  <input
                    type="email"
                    id="work_email"
                    value={formData.work_email || formData.email || ''}
                    onChange={(e) => handleFieldChange('work_email', e.target.value)}
                    className={validationErrors.work_email ? 'error' : ''}
                    placeholder="Enter work email address"
                  />
                  {validationErrors.work_email && (
                    <span className="error-text">{validationErrors.work_email[0]}</span>
                  )}
                </div>

                <div className="form-group">
                  <PhoneField
                    id="personal_phone"
                    label="Personal Phone"
                    value={formData.personal_phone}
                    onChange={(v) => handleFieldChange('personal_phone', v)}
                    error={validationErrors.personal_phone && validationErrors.personal_phone[0]}
                    placeholder="Enter personal phone number"
                  />
                </div>

                {/* Additional Phone Numbers */}
                <div className="form-group full-width">
                  <div className="additional-phone-numbers-header">
                    <label>Additional Phone Numbers</label>
                    <div className="phone-actions">
                      {additionalPhoneNumbers.length > 0 && !hasChanges && !phoneNumbersEditMode && (
                        <button
                          type="button"
                          onClick={() => setPhoneNumbersEditMode(true)}
                          className="edit-phone-button"
                          title="Edit phone numbers"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          Edit
                        </button>
                      )}
                      {(hasChanges || phoneNumbersEditMode || additionalPhoneNumbers.length === 0) && (
                        <button
                          type="button"
                          onClick={handleAddPhoneNumber}
                          className="add-phone-button"
                          title="Add phone number"
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {additionalPhoneNumbers.length > 0 && (
                    <div className="additional-phone-numbers-container-compact">
                      {additionalPhoneNumbers.map((phone, index) => (
                        <div key={phone.id} className="phone-number-item-compact">
                          <div className="phone-number-content">
                            <span className="phone-label">{phone.label || 'Unlabeled'}</span>
                            <span className="phone-number-display">
                              {phone.country_code} {phone.number}
                            </span>
                          </div>
                          {(hasChanges || phoneNumbersEditMode) && (
                            <div className="phone-number-edit-controls">
                              <input
                                type="text"
                                value={phone.label || ''}
                                onChange={(e) => handleUpdatePhoneNumber(phone.id, 'label', e.target.value)}
                                placeholder="Label"
                                className="phone-label-input-compact"
                                maxLength="50"
                              />
                              <CountryCodeSelect
                                value={phone.country_code || '+27'}
                                onChange={(code) => handleUpdatePhoneNumber(phone.id, 'country_code', code)}
                                className="country-code-select-compact"
                              />
                              <input
                                type="tel"
                                value={phone.number || ''}
                                onChange={(e) => handleUpdatePhoneNumber(phone.id, 'number', e.target.value)}
                                placeholder="Number"
                                className="phone-number-input-compact"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemovePhoneNumber(phone.id)}
                                className="remove-phone-button-compact"
                                title="Remove"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {additionalPhoneNumbers.length === 0 && (
                    <p className="no-phone-numbers-message">
                      No additional phone numbers. Click "Add" to add one.
                    </p>
                  )}
                </div>
                </div>
              </div>

              {/* Working With Me Guide Section */}
              <div className="form-section">
                <h2>Working With Me Guide</h2>
              <p className="section-description">
                Select characteristics that describe your working style and communication preferences.
              </p>
              
              <div className="working-characteristics">
                <h3>Working Characteristics</h3>
                <div className="characteristics-grid">
                  {dropdownOptions.workingCharacteristics?.map(char => (
                    <label key={char.value} className="working-characteristics-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.workingCharacteristics?.includes(char.value) || false}
                        onChange={(e) => handleWorkingCharacteristicsChange(char.value, e.target.checked)}
                        disabled={!formData.workingCharacteristics?.includes(char.value) && formData.workingCharacteristics?.length >= 10}
                      />
                      <span>{char.label}</span>
                    </label>
                  ))}
                </div>
                {validationErrors.working_characteristics && (
                  <span className="error-text">{validationErrors.working_characteristics[0]}</span>
                )}
              </div>

              <div className="custom-guide">
                <h3>Custom Working Guide</h3>
                <textarea
                  id="working_guide_custom"
                  value={formData.working_guide_custom || ''}
                  onChange={(e) => handleFieldChange('working_guide_custom', e.target.value)}
                  placeholder="Share additional information about your working style, communication preferences, time zones, or any other details that help colleagues work effectively with you..."
                  rows={4}
                  maxLength={500}
                />
                <div className={`character-count ${(formData.working_guide_custom || '').length > 450 ? 'approaching-limit' : ''}`}>
                  {(formData.working_guide_custom || '').length}/500 characters
                </div>
                </div>
              </div>

              {/* Employee Information Display - HIDDEN */}
              {/* <div className="form-section readonly-info">
              <h2>Employee Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>IMT Employee ID</label>
                  <p>{profile?.hms_employee_id || 'Not assigned'}</p>
                </div>
                <div className="info-item">
                  <label>Email Address</label>
                  <p>{profile?.email}</p>
                </div>
                <div className="info-item">
                  <label>Account Created</label>
                  <p>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Last Updated</label>
                  <p>{profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                </div>
              </div> */}

            </div>
          </div>
        </div>
        
        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* HMS Footer */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(AccountInformation);
